/**
 * Centralized utility functions for transaction formatting and processing
 * Shared across CoinDetails, History, and Activity components
 */

export const formatTransactionType = (transaction) => {
    // Rango Swaps
    if (transaction.swaps) return "Swap";

    // Use the type from API if available, otherwise determine from addresses
    if (transaction.type) {
        const type = transaction.type.toLowerCase();
        return type.charAt(0).toUpperCase() + type.slice(1);
    }

    // Logic from CoinDetails
    if (transaction.to === "public wallet") return "Send";

    // Logic from ActivityView/History
    const to = Array.isArray(transaction.to) ? transaction.to[0] : transaction.to;

    if (to === "public wallet") return "Send";
    return "Receive";
};

export const formatTransactionAmount = (transaction) => {
    // Rango Swaps
    if (transaction.swaps) {
        const fromSwap = transaction.swaps[0];
        const toSwap = transaction.swaps[transaction.swaps.length - 1];
        const fromAmount = parseFloat(fromSwap.fromAmount).toFixed(4);
        const toAmount = parseFloat(transaction.outputAmount || toSwap.toAmount).toFixed(4);
        return `${fromAmount} ${fromSwap.from.symbol} → ${toAmount} ${toSwap.to.symbol}`;
    }

    const amount = parseFloat(transaction.amount);
    const type = (transaction.type || '').toLowerCase();

    // Determine prefix based on type or to address
    let prefix = "+";
    if (type === "send" || type === "unshield" || transaction.to === "public wallet") {
        prefix = "-";
    }

    // Try to find token symbol or default to KAXAA
    const token = transaction.token || transaction.tokenId || transaction.symbol || "KAXAA";

    // Handle wei conversion if it looks like wei (very large integer)
    let displayAmount = amount;
    if (amount > 1e10) {
        displayAmount = amount / 1e18;
    }

    return `${prefix}${displayAmount.toFixed(6)} ${token}`;
};

export const formatTransactionAddress = (transaction) => {
    // Rango Swaps - show blockchain route
    if (transaction.swaps) {
        const fromSwap = transaction.swaps[0];
        const toSwap = transaction.swaps[transaction.swaps.length - 1];
        return `${fromSwap.from.blockchain} → ${toSwap.to.blockchain}`;
    }

    const type = formatTransactionType(transaction).toLowerCase();
    const address = type === "send" ? transaction.to : transaction.from;

    const finalAddress = Array.isArray(address) ? address[0] : address;

    if (!finalAddress || finalAddress === "public wallet") return "Public Wallet";
    return finalAddress.length > 20 ? `${finalAddress.slice(0, 10)}...${finalAddress.slice(-8)}` : finalAddress;
};

export const formatTransactionDate = (transaction) => {
    try {
        const timestamp = transaction.date || transaction.timestamp || (transaction.swaps && transaction.swaps[0].date) || transaction.createdAt;
        if (!timestamp) return 'Unknown';

        return new Date(timestamp).toLocaleString('en-US', {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
            hour: 'numeric',
            minute: '2-digit',
            hour12: true
        });
    } catch (error) {
        return transaction.date || transaction.timestamp || 'Unknown';
    }
};

export const getTransactionHash = (transaction) => {
    return transaction.requestId || transaction.hash || transaction.txHash || transaction.transactionHash || transaction.txId;
};

export const getStatusClass = (status) => {
    const s = (status || '').toLowerCase();
    if (s === "confirmed" || s === "completed" || s === "success") return "badge badge-success";
    if (s === "pending" || s === "initiated" || s === "processing" || s === "in-progress") return "badge badge-warning";
    return "badge badge-danger";
};
